import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'
import ApiKeysManager from '../../components/ApiKeysManager'
import Toggle from '../../components/Toggle'

/**
 * Per-reseller admin detail page.
 *
 * Sections:
 *   - Account header (name, owner, status, slug, currency)
 *   - Status actions (Suspend / Reactivate / Close)
 *   - Usage at-a-glance (users, courses, groupings, packages, bookings)
 *   - Fee rates editor — every active fee with effective % and override toggle
 *
 * Per Todd's restructure: this is where per-reseller fee management lives now.
 * The standalone /admin/fee-settings page only shows global defaults.
 */
export default function ResellerAccountDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get(`/admin/resellers/accounts/${id}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [id])

  if (loading) return <AdminShell title="Loading…"><div style={{ color: '#64748b' }}>Loading…</div></AdminShell>
  if (error || !data) {
    return (
      <AdminShell title="Reseller">
        <div style={errorBox}>{error || 'Not found'}</div>
        <Link to="/admin/reseller-accounts" style={linkStyle}>← All accounts</Link>
      </AdminShell>
    )
  }

  const a = data.account

  return (
    <AdminShell title={a.Name}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/admin/reseller-accounts" style={linkStyle}>← All accounts</Link>
      </div>

      <Section title="Account">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <Field label="Name" value={a.Name} />
          <Field label="Slug" value={a.Slug} mono />
          <Field label="Status" value={<StatusBadge value={a.Status} />} />
          <Field label="Currency" value={a.Currency} mono />
          <Field
            label="Owner"
            value={a.OwnerEmail
              ? <>{a.OwnerFirstName} {a.OwnerLastName}<div style={{ color: '#64748b', fontSize: 12 }}>{a.OwnerEmail}</div></>
              : <span style={{ color: '#94a3b8' }}>—</span>}
          />
          <Field label="Contact email" value={a.ContactEmail || <span style={{ color: '#94a3b8' }}>—</span>} />
          <Field label="Custom domain" value={a.CustomDomain ? <code style={{ fontSize: 12 }}>{a.CustomDomain} <span style={{ color: '#64748b' }}>({a.CustomDomainStatus})</span></code> : <span style={{ color: '#94a3b8' }}>default subdomain</span>} />
          <Field label="Created" value={new Date(a.DOE).toLocaleDateString()} />
        </div>
      </Section>

      <Section title="Usage">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <Stat label="Users"      value={data.usage.users} />
          <Stat label="Courses"    value={data.usage.courses} />
          <Stat label="Groupings"  value={data.usage.groupings} />
          <Stat label="Packages"   value={data.usage.packages} />
          <Stat label="Bookings"   value={data.usage.bookings} />
        </div>
      </Section>

      <UsersSection accountId={a.ID_NO} users={data.users} />

      <FeeRatesSection accountId={a.ID_NO} fees={data.fees} onChanged={reload} />

      <Section title="API access" subtitle="Issue API credentials so this reseller can drive courses, hotels, tee-times and bookings programmatically — hand these to them alongside the embed code.">
        <ApiKeysManager mode="admin" accountId={a.ID_NO} />
      </Section>

      <ChildTermsControlsSection account={a} onChanged={reload} />

      <StatusActionsSection account={a} onChanged={reload} onClosed={() => navigate('/admin/reseller-accounts')} />
    </AdminShell>
  )
}

// ─── Child Terms of Use controls ─────────────────────────────────────────
// Two per-reseller switches: (A) let this reseller author their own Terms for
// their child accounts; (B) whether those children must ALSO accept InteGolf's
// platform terms. B only shows when A is on; turning A off resets B to "on"
// server-side. The "parent-only" effect of B-off is itself safeguarded on the
// backend (only applies once the parent has actually published).
function ChildTermsControlsSection({ account, onChanged }) {
  const [authoring, setAuthoring] = useState(account.ChildTermsAuthoringEnabled === 1)
  const [accepts, setAccepts] = useState(account.ChildAcceptsIntegolfTerms !== 0)
  const [busy, setBusy] = useState(false)

  async function patch(body, optimistic) {
    setBusy(true)
    try {
      await tournamentApi.patch(`/admin/resellers/accounts/${account.ID_NO}`, body)
      optimistic?.()
      onChanged?.()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setBusy(false) }
  }

  function toggleAuthoring(next) {
    // Turning authoring off resets "accept InteGolf" to on (server-enforced).
    patch({ childTermsAuthoringEnabled: next }, () => { setAuthoring(next); if (!next) setAccepts(true) })
  }
  function toggleAccepts(next) {
    patch({ childAcceptsIntegolfTerms: next }, () => setAccepts(next))
  }

  return (
    <Section title="Child Terms of Use" subtitle="Let this reseller publish their own Terms of Use for the accounts they invite.">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <Toggle checked={authoring} onChange={toggleAuthoring} disabled={busy} />
          <div>
            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>Allow authoring of child terms</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              When on, this reseller gets a “Child Terms” screen to copy InteGolf’s latest terms, edit them, and publish versions their child accounts must accept.
            </div>
          </div>
        </div>
        {authoring && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, paddingLeft: 8, borderLeft: '3px solid #e2e8f0' }}>
            <Toggle checked={accepts} onChange={toggleAccepts} disabled={busy} />
            <div>
              <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>Children must also accept InteGolf’s terms</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                On (default): children accept both InteGolf’s platform terms and this reseller’s terms. Off: children accept only this reseller’s terms — but this only takes effect once the reseller has published their own (otherwise InteGolf’s still apply).
              </div>
            </div>
          </div>
        )}
      </div>
    </Section>
  )
}

// ─── Users + password reset ─────────────────────────────────────────────
function UsersSection({ accountId, users }) {
  const [busyId, setBusyId] = useState(null)
  const [result, setResult] = useState(null) // { email, emailSent, emailError, resetUrl }

  async function sendReset(u) {
    if (!confirm(`Send a password-reset link to ${u.email}?`)) return
    setBusyId(u.ID_NO); setResult(null)
    try {
      const { data } = await tournamentApi.post(`/admin/resellers/accounts/${accountId}/users/${u.ID_NO}/password-reset`, {})
      setResult(data)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setBusyId(null) }
  }

  const badge = (text, bg, fg) => <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: bg, color: fg }}>{text}</span>

  return (
    <Section title="Users" subtitle="People who can sign into this reseller account. Send a reset link if someone is locked out — it emails them a 15-minute link (and shows you a copyable fallback).">
      {(!users || users.length === 0) ? (
        <div style={{ color: '#94a3b8', fontSize: 14 }}>No users on this account yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {users.map(u => (
            <div key={u.ID_NO} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>
                  {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                  {u.IsOwner ? badge('OWNER', '#dbeafe', '#1d4ed8') : null}
                  {!u.is_active ? badge('INACTIVE', '#f1f5f9', '#64748b') : null}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{u.email}</div>
              </div>
              <button
                type="button"
                disabled={busyId === u.ID_NO || !u.is_active}
                onClick={() => sendReset(u)}
                style={{ padding: '8px 14px', background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: u.is_active ? 'pointer' : 'not-allowed', opacity: (busyId === u.ID_NO || !u.is_active) ? 0.6 : 1 }}
              >
                {busyId === u.ID_NO ? 'Sending…' : 'Send reset link'}
              </button>
            </div>
          ))}
        </div>
      )}
      {result && (
        <div style={{ marginTop: 12, padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 600, color: '#065f46', marginBottom: 6 }}>
            Reset link {result.emailSent ? `emailed to ${result.email}` : 'created — email failed, copy the link below'}
          </div>
          {result.emailError && <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>{result.emailError}</div>}
          <code style={{ display: 'block', background: '#fff', padding: '8px 10px', borderRadius: 6, fontSize: 12, wordBreak: 'break-all' }}>{result.resetUrl}</code>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => navigator.clipboard.writeText(result.resetUrl)} style={{ padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Copy link</button>
            <button type="button" onClick={() => setResult(null)} style={{ padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Dismiss</button>
          </div>
        </div>
      )}
    </Section>
  )
}

// ─── Status actions ─────────────────────────────────────────────────────
function StatusActionsSection({ account, onChanged, onClosed }) {
  const [busy, setBusy] = useState(false)
  async function patch(status) {
    setBusy(true)
    try {
      await tournamentApi.patch(`/admin/resellers/accounts/${account.ID_NO}`, { status })
      onChanged()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function close() {
    if (!confirm(`Close ${account.Name}?\n\nThe account stays in the database (bookings, packages, users preserved for audit) but reseller users can no longer log in. This is reversible — set status back to active to restore access.`)) return
    setBusy(true)
    try {
      await tournamentApi.delete(`/admin/resellers/accounts/${account.ID_NO}`)
      onClosed()
    } catch (e) { alert(e?.response?.data?.message || e.message); setBusy(false) }
  }
  return (
    <Section title="Status">
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {account.Status === 'active' && (
          <>
            <button onClick={() => patch('suspended')} disabled={busy} style={warnBtn}>
              Suspend account
            </button>
            <button onClick={close} disabled={busy} style={dangerBtn}>
              Close account
            </button>
          </>
        )}
        {account.Status === 'suspended' && (
          <>
            <button onClick={() => patch('active')} disabled={busy} style={primaryBtn}>
              Reactivate
            </button>
            <button onClick={close} disabled={busy} style={dangerBtn}>
              Close account
            </button>
          </>
        )}
        {account.Status === 'pending' && (
          <>
            <button onClick={() => patch('active')} disabled={busy} style={primaryBtn}>
              Activate now
            </button>
            <button onClick={close} disabled={busy} style={dangerBtn}>
              Close account
            </button>
          </>
        )}
        {account.Status === 'closed' && (
          <>
            <span style={{ color: '#64748b', fontSize: 13 }}>Account is closed. Re-activate to restore login access:</span>
            <button onClick={() => patch('active')} disabled={busy} style={primaryBtn}>
              Reactivate account
            </button>
          </>
        )}
      </div>
      <p style={{ fontSize: 12, color: '#64748b', marginTop: 12, lineHeight: 1.5 }}>
        <strong>Suspend:</strong> reseller users can't log in but data stays. Reversible.<br/>
        <strong>Close:</strong> soft-delete. Same as suspend but signals the relationship has ended. Reversible.<br/>
        Hard delete is not exposed — contact engineering if you truly need to wipe the account from the database.
      </p>
    </Section>
  )
}

// ─── Fee rates editor ───────────────────────────────────────────────────
function FeeRatesSection({ accountId, fees, onChanged }) {
  return (
    <Section title="Fee rates" subtitle="Override any default for this reseller (volume deals, founder pricing, etc.). Blank = use the global default.">
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={th}>Fee</th>
              <th style={th}>Default</th>
              <th style={th}>Effective for this reseller</th>
              <th style={th}>Note</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {fees.map(f => (
              <FeeRow key={f.feeKey + ':' + (f.providerClass || 'global')} accountId={accountId} fee={f} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  )
}
function FeeRow({ accountId, fee, onChanged }) {
  // Show percent as percent (5.00 not 0.05). Empty string when no override.
  const initialPct = fee.isOverride ? (fee.overridePercent * 100).toFixed(4).replace(/\.?0+$/, '') : ''
  const [pct, setPct] = useState(initialPct)
  const [note, setNote] = useState(fee.overrideNote || '')
  // v158: hard-cap toggle. Only shown on golf_markup rows; only allowed
  // when the percent is exactly 0 (the platform won't subsidise an
  // arbitrary loss on top of a non-zero markup).
  const initialHardCap = !!fee.allowHardCap
  const [hardCap, setHardCap] = useState(initialHardCap)
  const [busy, setBusy] = useState(false)
  const isGolfMarkup = fee.feeKey === 'golf_markup'
  const pctIsZero = pct !== '' && Number(pct) === 0
  const dirty = pct !== initialPct || note !== (fee.overrideNote || '') || hardCap !== initialHardCap

  // If the percent moves off 0, the checkbox forcibly clears -- mirrors
  // the server's write-side guard so the UI doesn't lie.
  useEffect(() => {
    if (!pctIsZero && hardCap) setHardCap(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pct])

  // v157: per-provider-class overrides. When fee.providerClass is set
  // (golf_markup split into one row per class), every API call carries
  // the providerClass slug so the backend writes the right row.
  const classQs = fee.providerClass ? `?providerClass=${encodeURIComponent(fee.providerClass)}` : ''
  async function save() {
    setBusy(true)
    try {
      if (pct === '') {
        if (fee.isOverride) {
          await tournamentApi.delete(`/admin/fee-settings/${fee.feeKey}/overrides/${accountId}${classQs}`)
        }
      } else {
        await tournamentApi.post(`/admin/fee-settings/${fee.feeKey}/overrides`, {
          resellerAccountId: Number(accountId),
          percentValue:      Number(pct) / 100,
          note:              note || null,
          providerClass:     fee.providerClass || undefined,
          // Only meaningful on golf_markup rows; harmless on others
          // (backend ignores allowHardCap if it can't be honoured).
          allowHardCap:      isGolfMarkup && pctIsZero ? hardCap : false,
        })
      }
      onChanged()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function clearOverride() {
    if (!fee.isOverride) return
    setBusy(true)
    try {
      await tournamentApi.delete(`/admin/fee-settings/${fee.feeKey}/overrides/${accountId}${classQs}`)
      onChanged()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  return (
    <tr style={{ borderTop: '1px solid #f1f5f9' }}>
      <td style={td}>
        <strong>{fee.label}</strong>
        <code style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>{fee.feeKey}</code>
        {fee.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, maxWidth: 360 }}>{fee.description}</div>}
      </td>
      <td style={tdMono}>{fee.defaultPercent != null ? `${(fee.defaultPercent * 100).toFixed(4).replace(/\.?0+$/, '')}%` : <span style={{ color: '#94a3b8' }}>—</span>}</td>
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="number" step="0.0001" min={0} max={100}
            value={pct}
            onChange={e => setPct(e.target.value)}
            placeholder={`${(fee.defaultPercent * 100).toFixed(2).replace(/\.?0+$/, '')} (default)`}
            style={{ ...inp, width: 110, textAlign: 'right' }}
          />
          <span style={{ color: '#64748b' }}>%</span>
          {fee.isOverride && (
            <span style={{ ...pill('#fef3c7', '#92400e'), marginLeft: 8 }}>custom</span>
          )}
        </div>
        {isGolfMarkup && (
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginTop: 6, fontSize: 12,
              color: pctIsZero ? '#475569' : '#94a3b8',
              cursor: pctIsZero ? 'pointer' : 'not-allowed',
            }}
            title={pctIsZero
              ? 'Lets this reseller pick the "Flat rate" pricing modes on their packages — the reseller commits to a fixed customer-facing price and absorbs any variance above it.'
              : 'Flat-rate pricing is only available when the markup is set to 0%.'}
          >
            <input
              type="checkbox"
              checked={hardCap}
              onChange={e => setHardCap(e.target.checked)}
              disabled={!pctIsZero}
            />
            Allow flat-rate pricing
          </label>
        )}
      </td>
      <td style={td}>
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Launch promo, founder pricing…"
          style={{ ...inp, width: 200 }}
        />
      </td>
      <td style={td}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={save} disabled={busy || !dirty} style={{ ...primaryBtnSm, opacity: (busy || !dirty) ? 0.5 : 1 }}>Save</button>
          {fee.isOverride && (
            <button onClick={clearOverride} disabled={busy} style={secondaryBtnSm}>Reset</button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Building blocks ────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 16, color: '#0f172a' }}>{title}</h2>
      {subtitle && <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>{subtitle}</div>}
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  )
}
function Field({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#0f172a', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  )
}
function Stat({ label, value }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    </div>
  )
}
function StatusBadge({ value }) {
  const colors = {
    pending:   { bg: '#fef3c7', fg: '#92400e' },
    active:    { bg: '#dcfce7', fg: '#166534' },
    suspended: { bg: '#fee2e2', fg: '#991b1b' },
    closed:    { bg: '#e2e8f0', fg: '#475569' },
  }
  const c = colors[value] || colors.closed
  return <span style={pill(c.bg, c.fg)}>{value}</span>
}
function pill(bg, fg) {
  return { display: 'inline-block', padding: '3px 8px', background: bg, color: fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }
}

const linkStyle = { color: '#475569', textDecoration: 'none', fontSize: 13 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const inp = { padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }
const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }
const td = { padding: '12px', verticalAlign: 'top' }
const tdMono = { ...td, fontFamily: 'monospace', fontSize: 13 }
const primaryBtn = { padding: '8px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const primaryBtnSm = { padding: '6px 12px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const secondaryBtnSm = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12, cursor: 'pointer' }
const warnBtn = { padding: '8px 16px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const dangerBtn = { padding: '8px 16px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
